import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO component for managing page metadata
 * @param {string} title - Page title
 * @param {string} description - Page description
 * @param {string} keywords - SEO keywords
 * @param {string} image - Open Graph image URL
 * @param {string} url - Canonical URL
 * @param {string} type - Open Graph type (default: 'website')
 */
function SEO({
  title = 'tiniego - Create Personalized 3D Figurines from Your Photos',
  description = 'Transform your cherished memories into unique 3D figurines. Upload your photos and let AI create personalized miniatures you can order and treasure forever.',
  keywords = '3D figurines, personalized gifts, AI photo conversion, custom miniatures, memory keepsakes',
  image = '/og-image.png',
  url,
  type = 'website',
}) {
  const siteTitle = 'tinego';
  const fullTitle = title.includes(siteTitle) ? title : `${title} | ${siteTitle}`;
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://tiniego.com';
  const canonicalUrl = url || typeof window !== 'undefined' ? window.location.href : siteUrl;
  const imageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content={siteTitle} />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={imageUrl} />

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="author" content="tiniego" />
    </Helmet>
  );
}

export default SEO;
